import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogOut, ExternalLink, Package, Key, BookOpen, MessageSquare, Settings, Menu, X } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const NAV = [
  { to: '/provider/dashboard', icon: LayoutDashboard, label: 'Mein Profil' },
  { to: '/provider/products',  icon: Package,         label: 'Produkte' },
  { to: '/provider/reviews',   icon: MessageSquare,   label: 'Bewertungen' },
  { to: '/provider/api-keys',  icon: Key,             label: 'API-Schlüssel' },
  { to: '/provider/api-docs',  icon: BookOpen,        label: 'API-Dokumentation' },
  { to: '/provider/settings',  icon: Settings,        label: 'Einstellungen' },
];

function SidebarContent({ user, onNav, handleLogout }) {
  return (
    <>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} onClick={onNav}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent'
              }`
            }>
            <Icon className="w-4 h-4 flex-shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-slate-800/60 space-y-0.5">
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors">
          <ExternalLink className="w-4 h-4 flex-shrink-0" />
          Website ansehen
        </a>
        <button onClick={handleLogout} data-testid="provider-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
          <LogOut className="w-4 h-4 flex-shrink-0" />
          Abmelden
        </button>
      </div>

      {user && (
        <div className="px-4 py-3.5 border-t border-slate-800/60 bg-slate-900/30">
          <p className="text-xs font-semibold text-white truncate">{user.name}</p>
          <p className="text-xs text-slate-500 truncate mt-0.5">{user.email}</p>
        </div>
      )}
    </>
  );
}

export function ProviderLayout({ children }) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/provider/login'); };
  const logoEl = (
    <a href="/" className="flex items-center gap-1 select-none">
      <span className="text-sm font-extrabold text-white uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Hosting-</span>
      <span className="text-sm font-extrabold text-cyan-400 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Community</span>
    </a>
  );

  return (
    <div className="min-h-screen bg-[#020817]">

      {/* ── Mobile top bar ── */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#0a1628] border-b border-slate-800/60">
        <div>
          {logoEl}
          <p className="text-xs text-slate-500">Anbieter-Portal</p>
        </div>
        <button onClick={() => setMobileOpen(true)} data-testid="provider-mobile-menu-btn"
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" aria-hidden="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0a1628] border-r border-slate-800/60 flex flex-col">
            <div className="px-5 py-5 border-b border-slate-800/60 flex items-center justify-between">
              <div>
                {logoEl}
                <p className="text-xs text-slate-500 mt-0.5">Anbieter-Portal</p>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg text-slate-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <SidebarContent user={user} onNav={() => setMobileOpen(false)} handleLogout={handleLogout} />
          </aside>
        </div>
      )}

      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:flex w-60 flex-shrink-0 bg-[#0a1628] border-r border-slate-800/60 flex-col fixed h-full z-10">
        <div className="px-5 py-5 border-b border-slate-800/60">
          {logoEl}
          <p className="text-xs text-slate-500 mt-0.5">Anbieter-Portal</p>
        </div>
        <SidebarContent user={user} onNav={() => {}} handleLogout={handleLogout} />
      </aside>

      {/* ── Main content ── */}
      <main className="md:ml-60 min-h-screen overflow-auto">
        {children}
      </main>
    </div>
  );
}
